// src/components/admin/VistaCambioAuditoria.js
//
// D-27 — Muestra el "antes y después" de una fila de auditoría.
//
// El backend guarda esos campos como jsonb y los devuelve como texto, así
// que acá se parsean. Cuando los dos lados son objetos planos se arma una
// tabla campo por campo y se resaltan solo los que cambiaron: es la forma
// de leer un cambio sin tener que comparar dos bloques de JSON a ojo.
//
// Si la estructura es anidada o un arreglo, se cae al JSON formateado —
// mejor mostrarlo crudo que mentir con una tabla que no corresponde.

import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Chip } from '@mui/material';

/** El jsonb llega como string; un valor inválido no debe tumbar la pantalla. */
export function parsearJson(valor) {
  if (valor == null) return null;
  if (typeof valor === 'object') return valor;
  try { return JSON.parse(valor); } catch { return { _sinParsear: String(valor) }; }
}

const esObjetoPlano = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && Object.values(v).every((x) => x === null || typeof x !== 'object');

const comoTexto = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(vacío)';
  if (typeof v === 'object') return JSON.stringify(v);
  // Las fechas ISO se vuelven legibles; lo demás queda tal cual.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(v) ? v : `${v}Z`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString('es-CO', {
        timeZone: 'America/Bogota', day: '2-digit', month: 'short',
        year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }
  }
  return String(v);
};

const Bloque = ({ titulo, valor }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>{titulo}</Typography>
    <Box
      component="pre"
      sx={{
        m: 0, mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'action.hover',
        fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}
    >
      {JSON.stringify(valor, null, 2)}
    </Box>
  </Box>
);

/**
 * @param {object} props
 * @param {*} props.antes    jsonb de la columna Antes (string u objeto)
 * @param {*} props.despues  jsonb de la columna Despues
 */
export default function VistaCambioAuditoria({ antes, despues }) {
  const a = parsearJson(antes);
  const d = parsearJson(despues);

  if (!a && !d) {
    return (
      <Typography variant="body2" color="text.secondary">
        Esta acción no registró estado anterior ni posterior.
      </Typography>
    );
  }

  // Una creación o una eliminación tiene un solo lado: no hay nada que comparar.
  if (!a || !d) {
    return <Bloque titulo={a ? 'Estado anterior' : 'Resultado'} valor={a || d} />;
  }

  if (!esObjetoPlano(a) || !esObjetoPlano(d)) {
    return (
      <>
        <Bloque titulo="Antes"   valor={a} />
        <Bloque titulo="Después" valor={d} />
      </>
    );
  }

  const campos = [...new Set([...Object.keys(a), ...Object.keys(d)])];

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Campo</TableCell>
          <TableCell>Antes</TableCell>
          <TableCell>Después</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {campos.map((campo) => {
          const va = comoTexto(a[campo]);
          const vd = comoTexto(d[campo]);
          const cambio = va !== vd;
          return (
            <TableRow key={campo} sx={cambio ? { bgcolor: 'warning.light', opacity: 0.95 } : undefined}>
              <TableCell sx={{ fontWeight: cambio ? 700 : 400 }}>{campo}</TableCell>
              <TableCell sx={{ fontSize: 13 }}>{va}</TableCell>
              <TableCell sx={{ fontSize: 13, fontWeight: cambio ? 700 : 400 }}>
                {vd}
                {cambio && <Chip size="small" label="cambió" sx={{ ml: 1 }} />}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
